import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { useReducer, useEffect, useState } from "react";
import { Checkbox } from "primereact/checkbox";
import classNames from "classnames";
import { Calendar } from "primereact/calendar";
import { Fieldset } from "primereact/fieldset";
import { Button } from "primereact/button";

import {
  CreateInvoiceSummaryDialogProps,
  createSummaryDefaultValues,
} from "./createInvoiceSummaryConsts";
import { SummaryDetailsTable } from "../summaryDetailsTable.tsx";
import { InvoiceSummaryService } from "../../InvoiceSummaryService";
import {
  InvoiceSummaryFieldType,
  ODataPeriod,
  OInvoiceSummaryField,
  CreateInvoiceSummaryFormState,
  OInvoiceGoodsType,
} from "../../../../../shared/constants";
import { ToastService } from "../../../../../AppToast";
import moment from "moment";
import { formatVatRate, getMQDropdownValues } from "../../invoiceSummaryUtils";
import { useTranslation } from "../../../../../shared/language";

export const CreateInvoiceSummaryDialog = ({
  onHide,
  open,
  currencies,
  refreshLazyData,
}: CreateInvoiceSummaryDialogProps) => {
  const { t } = useTranslation();
  const [summaryDataDetails, setSummaryDataDetails] = useState([]);
  const [availableSerials, setAvailableSerials] = useState([]);
  const [selectedSerial, setSelectedSerial] = useState("");
  const [disableSubmit, setDisableSubmit] = useState(false);

  const [formState, dispatch] = useReducer(
    (
      state: CreateInvoiceSummaryFormState,
      action: { type: InvoiceSummaryFieldType | string; payload: any }
    ) => {
      switch (action.type) {
        case OInvoiceSummaryField.DATA_PERIOD:
          return { ...state, dataPeriod: action.payload };
        case OInvoiceSummaryField.DATA_PERIOD_DATE:
          return { ...state, dataPeriodDate: action.payload };
        case OInvoiceSummaryField.MONTH_OR_QUARTER:
          return { ...state, monthOrQuarter: action.payload };
        case OInvoiceSummaryField.YEAR:
          return { ...state, year: action.payload };
        case OInvoiceSummaryField.CREATION_DATE:
          return { ...state, creationDate: action.payload };
        case OInvoiceSummaryField.IS_FIRST_SUBMISSION:
          return { ...state, isFirstSubmission: action.payload };
        case OInvoiceSummaryField.CURRENCY_CODE:
          return { ...state, currencyCode: action.payload };
        case OInvoiceSummaryField.STATUS:
          return { ...state, status: action.payload };
        case OInvoiceSummaryField.TOTAL_SUBMIT:
          return { ...state, totalSubmit: action.payload };
        case OInvoiceSummaryField.SUMMARY_DATA_NUMBER:
          return { ...state, summaryDataNumber: action.payload };
        case OInvoiceSummaryField.GOODS_TYPE:
          return { ...state, goodsType: action.payload };
        case "multiTax":
          return { ...state, multiTax: action.payload };
        case "all":
          return { ...state, ...action.payload };
        default:
          return state;
      }
    },
    createSummaryDefaultValues
  );

  const handleSubmit = () => {
    const validateFormState = (
      state: CreateInvoiceSummaryFormState
    ): string[] => {
      const errors: string[] = [];

      // Validate dataPeriod
      if (!state.dataPeriod) {
        errors.push(t("validation.required", { field: t("report.dataPeriod") }));
      }

      if (!state.creationDate) {
        errors.push(t("validation.required", { field: t("common.invoiceCreationDate") }));
      }

      // Validate dataPeriodDate for daily period
      if (state.dataPeriod === ODataPeriod.DAY && !state.dataPeriodDate) {
        errors.push(t("validation.required", { field: t("report.day") }));
      }

      // Validate monthOrQuarter for monthly/quarterly periods
      if (state.dataPeriod !== ODataPeriod.DAY) {
        if (!state.monthOrQuarter) {
          errors.push(t("validation.required", { field: t("report.monthOrQuarter") }));
        } else {
          const value = parseInt(state.monthOrQuarter);
          if (
            state.dataPeriod === ODataPeriod.MONTH &&
            (value < 1 || value > 12)
          ) {
            errors.push(t("validation.monthRange"));
          }
          if (
            state.dataPeriod === ODataPeriod.QUARTER &&
            (value < 1 || value > 4)
          ) {
            errors.push(t("validation.quarterRange"));
          }
        }
      }

      // Validate year
      if (state.dataPeriod !== ODataPeriod.DAY && !state.year) {
        errors.push(t("validation.required", { field: t("report.year") }));
      }
      if (state.dataPeriod !== ODataPeriod.DAY && !/^\d{4}$/.test(state.year)) {
        errors.push(t("validation.yearFormat"));
      }

      // Validate currencyCode
      if (!state.currencyCode) {
        errors.push(t("validation.required", { field: t("report.currencyCode") }));
      }

      if (!state.goodsType) {
        errors.push(t("validation.required", { field: t("report.goodsType") }));
      }

      if (!state.isFirstSubmission && !state.totalSubmit) {
        errors.push(t("validation.required", { field: t("report.supplementCount") }));
      }

      if (summaryDataDetails.length === 0) {
        errors.push(t("validation.required", { field: t("common.selectInvoice") }));
      }

      return errors;
    };

    const validationErrors = validateFormState(formState);

    if (validationErrors.length > 0) {
      ToastService.error(validationErrors[0]);
      return;
    }

    const orderedSummaryDataDetails = summaryDataDetails.map(
      (detail: any, index: number) => ({
        ...detail,
        orders: index + 1,
        vatRate: formatVatRate(detail?.vatRate?.toString()),
      })
    );
    setDisableSubmit(true);
    InvoiceSummaryService.createInvoiceSummary({
      summaryDataDetails: orderedSummaryDataDetails,
      creationDate: moment(formState.creationDate).format("YYYY-MM-DD"),
      currencyCode: formState.currencyCode,
      dataPeriod: formState.dataPeriod,
      note: null,
      goodsType: formState.goodsType,
      ...(formState.dataPeriod !== ODataPeriod.DAY && {
        monthOrQuarter: parseInt(formState.monthOrQuarter),
        year: parseInt(formState.year),
      }),
      ...(formState.dataPeriod === ODataPeriod.DAY && {
        dataPeriodDate: moment(formState.dataPeriodDate).format("YYYY-MM-DD"),
      }),
    })
      .then(() => {
        onHide(false);
        dispatch({ payload: createSummaryDefaultValues, type: "all" });

        refreshLazyData();
      })
      .finally(() => setDisableSubmit(false));
  };

  const footer = (
    <div className='flex justify-content-center'>
      <Button
        label={t("common.close")}
        onClick={() => {
          onHide(false);
          dispatch({ payload: createSummaryDefaultValues, type: "all" });
        }}
        className='p-button-outlined p-button-secondary p-button-sm mr-2'
      />
      <Button
        label={t("common.save")}
        className='p-button-outlined p-button-sm mr-2'
        disabled={disableSubmit}
        onClick={() => {
          handleSubmit();
        }}
      />
    </div>
  );

  useEffect(() => {
    InvoiceSummaryService.getValidSerials().then((data) => {
      setAvailableSerials(
        data.map((item) => {
          return { label: item.invoiceSerial, value: item.invoiceSerial };
        })
      );
    });
  }, []);

  useEffect(() => {
    if (open) {
      setSummaryDataDetails([]);
      setSelectedSerial("");
    }
  }, [open]);

  return (
    <Dialog
      className='bg-theme p-dialog'
      header={t("report.summaryCreateTitle")}
      footer={footer}
      visible={open}
      maximized={true}
      onHide={() => {
        onHide(false);
        dispatch({ payload: createSummaryDefaultValues, type: "all" });
      }}
      headerStyle={{
        padding: "0.5rem 1.5rem",
      }}
    >
      <div className='grid mt-1'>
        <div className='col-12 form-xs flex align-items-center justify-content-center'>
          <Fieldset
            style={{ width: "100%" }}
            legend={t("report.summaryFindInvoices")}
            className='mt-1 pl-3 pr-3 pt-2 col-12 md:col-6 lg:col-4 p-fieldset'
          >
            <form className='flex justify-content-between align-items-start flex-wrap'>
              <div className='grid gap-2 w-full md:w-6 xl:w-4 md:pr-2'>
                <div
                  className={classNames("field align-items-center grid", {
                    "col-7": formState.dataPeriod !== ODataPeriod.DAY,
                    "col-12 flex-auto":
                      formState.dataPeriod === ODataPeriod.DAY,
                  })}
                >
                  <label
                    className='col-4'
                    htmlFor='name'
                    style={{ width: "6.75rem" }}
                  >
                    {t("report.dataPeriod")}
                    <span className='required'>*</span>
                  </label>
                  <div
                    className={classNames(
                      "col-8 grid align-items-center pb-0 pt-2 h-full",
                      {
                        "flex-auto": formState.dataPeriod === ODataPeriod.DAY,
                      }
                    )}
                  >
                    <Dropdown
                      className=' col-6 pt-0 pb-0'
                      id='name'
                      value={formState.dataPeriod}
                      options={[
                        {
                          label: t("report.day"),
                          value: ODataPeriod.DAY,
                        },
                        {
                          label: t("report.month"),
                          value: ODataPeriod.MONTH,
                        },
                        {
                          label: t("report.quarter"),
                          value: ODataPeriod.QUARTER,
                        },
                      ]}
                      onChange={(e) => {
                        if (e.value === ODataPeriod.DAY) {
                          dispatch({
                            type: OInvoiceSummaryField.YEAR,
                            payload: "",
                          });
                          dispatch({
                            type: OInvoiceSummaryField.MONTH_OR_QUARTER,
                            payload: "",
                          });
                          dispatch({
                            type: OInvoiceSummaryField.DATA_PERIOD_DATE,
                            payload: new Date(),
                          });
                        }
                        if (e.value === ODataPeriod.MONTH) {
                          dispatch({
                            type: OInvoiceSummaryField.MONTH_OR_QUARTER,
                            payload: new Date().getMonth() + 1,
                          });
                          dispatch({
                            type: OInvoiceSummaryField.YEAR,
                            payload: new Date().getFullYear(),
                          });
                        }
                        if (e.value === ODataPeriod.QUARTER) {
                          dispatch({
                            type: OInvoiceSummaryField.MONTH_OR_QUARTER,
                            payload: Math.floor(new Date().getMonth() / 3 + 1),
                          });
                          dispatch({
                            type: OInvoiceSummaryField.YEAR,
                            payload: new Date().getFullYear(),
                          });
                        }
                        dispatch({
                          type: OInvoiceSummaryField.DATA_PERIOD,
                          payload: e.value,
                        });
                      }}
                    />
                    <div className='col-6 p-0 pl-1 h-full flex align-items-center'>
                      {formState.dataPeriod === ODataPeriod.DAY && (
                        <Calendar
                          className='w-full'
                          id='typeInvoiceId'
                          value={formState.dataPeriodDate}
                          dateFormat={"dd/mm/yy"}
                          onChange={(e) =>
                            dispatch({
                              type: OInvoiceSummaryField.DATA_PERIOD_DATE,
                              payload: e.value,
                            })
                          }
                        />
                      )}
                      {formState.dataPeriod !== ODataPeriod.DAY && (
                        <Dropdown
                          className='w-full '
                          options={getMQDropdownValues(formState.dataPeriod)}
                          placeholder={t("report.summaryPeriodPlaceholder")}
                          value={formState.monthOrQuarter}
                          onChange={(e) => {
                            dispatch({
                              type: OInvoiceSummaryField.MONTH_OR_QUARTER,
                              payload: e.target.value,
                            });
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div
                  className={classNames(
                    "field align-items-center grid flex-auto",
                    {
                      "col-5": formState.dataPeriod !== ODataPeriod.DAY,
                      hidden: formState.dataPeriod === ODataPeriod.DAY,
                    }
                  )}
                >
                  <label className='col-3' htmlFor='typeInvoiceId'>
                    {t("report.year")}
                    <span className='required'>*</span>
                  </label>
                  <div className='col-9'>
                    <InputText
                      className=' w-full'
                      id='typeInvoiceId'
                      disabled={formState.dataPeriod === ODataPeriod.DAY}
                      value={formState.year}
                      onChange={(e) =>
                        dispatch({
                          type: OInvoiceSummaryField.YEAR,
                          payload: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className='field flex col-4 align-items-center'>
                  <label
                    className='mt-0 mb-0'
                    htmlFor='typeInvoiceId'
                    style={{ minWidth: "6.25rem" }}
                  >
                    {t("report.firstSubmission")}
                  </label>
                  <Checkbox
                    disabled
                    checked={formState.isFirstSubmission}
                    onChange={(e) =>
                      dispatch({
                        type: OInvoiceSummaryField.IS_FIRST_SUBMISSION,
                        payload: e.checked,
                      })
                    }
                  />
                </div>
                <div className='field grid col-8 align-items-center flex-auto'>
                  <label className='mt-0 col-4 mb-0' htmlFor='typeInvoiceId'>
                    {t("report.supplementCount")}
                  </label>
                  <div className='col-8 '>
                    <InputText
                      disabled={formState.isFirstSubmission}
                      className='w-full'
                      value={formState.totalSubmit}
                      onChange={(e) =>
                        dispatch({
                          type: OInvoiceSummaryField.TOTAL_SUBMIT,
                          payload: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
              <div className='grid gap-2 w-full md:w-6 xl:w-4 xl:pr-2'>
                <div className='field align-items-center grid col-6'>
                  <label
                    className='col-5'
                    htmlFor='typeInvoiceId'
                    style={{ width: "5rem" }}
                  >
                    {t("report.invoiceNumberShort")}
                  </label>
                  <div className='col-7'>
                    <InputText
                      disabled={formState.isFirstSubmission}
                      className=' w-full'
                      id='typeInvoiceId'
                      value={formState.summaryDataNumber}
                      onChange={(e) =>
                        dispatch({
                          type: OInvoiceSummaryField.SUMMARY_DATA_NUMBER,
                          payload: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className='field align-items-center grid col-6 flex-auto'>
                  <label className='col-4' htmlFor='typeInvoiceId'>
                    {t("common.invoiceCreationDate")}
                    <span className='required'>*</span>
                  </label>
                  <div className='col-8 pl-0'>
                    <Calendar
                      className=' w-full'
                      dateFormat='dd/mm/yy'
                      value={formState.creationDate}
                      onChange={(e) => {
                        dispatch({
                          type: OInvoiceSummaryField.CREATION_DATE,
                          payload: e.value,
                        });
                      }}
                    />
                  </div>
                </div>
                <div className='field grid align-items-center col-12 flex-auto'>
                  <label
                    className=''
                    style={{ minWidth: "5.25rem", paddingLeft: 3 }}
                  >
                    {t("common.symbol")}
                    <span className='required'>*</span>
                  </label>
                  <Dropdown
                    name='invoiceSerial'
                    style={{ flex: "1 auto" }}
                    value={selectedSerial}
                    onChange={(e) => setSelectedSerial(e.value)}
                    options={availableSerials}
                    placeholder={t("report.summaryInvoiceSerialPlaceholder")}
                    filter
                  />
                </div>
              </div>
              <div className='grid gap-2 w-full md:w-6 xl:w-4'>
                <div className='field grid col-7 align-items-center'>
                  <label className='col-5' htmlFor='name'>
                    {t("report.goodsType")}
                    <span className='required'>*</span>
                  </label>
                  <div className='col-7 pl-0'>
                    <Dropdown
                      className=' w-full'
                      id='name'
                      value={formState.goodsType}
                      options={[
                        {
                          label: t("report.otherGoods"),
                          value: OInvoiceGoodsType.OTHERS,
                        },
                        {
                          label: t("report.flightGoods"),
                          value: OInvoiceGoodsType.FLIGHT_GOODS,
                        },
                      ]}
                      onChange={(e) => {
                        dispatch({
                          type: OInvoiceSummaryField.GOODS_TYPE,
                          payload: e.value,
                        });
                      }}
                    />
                  </div>
                </div>

                <div className='field grid col-5 algin-items-center flex-auto'>
                  <label className='col-5' htmlFor='name'>
                    {t("report.currencyCode")}
                    <span className='required'>*</span>
                  </label>
                  <div className='col-7 flex align-items-center'>
                    <Dropdown
                      filter
                      className=' w-full'
                      id='name'
                      value={formState.currencyCode}
                      disabled={summaryDataDetails.length > 0}
                      options={currencies.map((currency) => {
                        return {
                          label: currency.code,
                          value: currency.code,
                        };
                      })}
                      onChange={(e) => {
                        dispatch({
                          type: OInvoiceSummaryField.CURRENCY_CODE,
                          payload: e.value,
                        });
                      }}
                    />
                  </div>
                </div>
                <div
                  className='field flex align-items-center gap-2 col-12 pl-0'
                  style={{ height: 27 }}
                >
                  <label
                    className='mt-0 mb-0'
                    htmlFor='typeInvoiceId'
                    style={{ minWidth: "7rem" }}
                  >
                    {t("report.multipleTax")}
                  </label>
                  <Checkbox
                    checked={formState.multiTax}
                    onChange={(e) =>
                      dispatch({
                        type: "multiTax",
                        payload: e.checked,
                      })
                    }
                  />
                </div>
              </div>
            </form>
          </Fieldset>
        </div>
        <div className='col-12'>
          <SummaryDetailsTable
            formState={formState}
            setSummaryDetails={setSummaryDataDetails}
            summaryDetails={summaryDataDetails}
            serial={selectedSerial}
          />
        </div>
      </div>
    </Dialog>
  );
};
